/**
 * "Export as JSON" in the AI Studio panel: one skill (with the building blocks it uses) or the whole agent,
 * as a pretty-printed JSON file the owner keeps (backup, documentation). Read-only, there is no import.
 * Data: GET /api/agent-studio/{botId} (the AgentSpec as the portal sees it; backend builder/service.py
 * public_spec, never secret values) and GET /api/agent-studio/{botId}/knowledge (the knowledge sources).
 * Pure builders, so the file's shape is unit-tested (skillExport.test.js).
 */
import { studioApi } from './studioApi';
import { fetchDocs } from './studioData';

export const EXPORT_FORMAT = 'ultimo-ai-studio-export/1';

// fields public_spec adds for the panel (computed per request, not stored on the skill / the agent), plus the
// platform's own parts: only the customer's content goes into the file (Robert, 05.11.2026)
const SKILL_VIEW_FIELDS = ['captures_leads', 'counts_toward_plan'];
const AGENT_VIEW_FIELDS = ['usage', 'models', 'limits', 'has_chat_window', 'organization_id', 'model', 'settings'];
const TOOL_INTERNAL_FIELDS = ['last_test'];
const INTEGRATION_INTERNAL_FIELDS = ['status_detail', 'mcp_tools'];
// a ready-made skill's procedure is Ultimo's tested template (backend agent_studio/packages): only the
// customer's own parts of it (name, summary, examples, settings, extra rules) are exported
const PACKAGE_OWNED_FIELDS = ['instructions', 'when_to_use'];

const isReadyMade = (skill) => !!skill?.package && !skill.package.detached;

/** The skill as the file shows it: a ready-made skill without Ultimo's procedure and own rules. */
export function exportSkill(skill) {
  const out = withoutKeys(skill, SKILL_VIEW_FIELDS);
  if (!isReadyMade(skill)) return out;
  PACKAGE_OWNED_FIELDS.forEach((k) => delete out[k]);
  const extra = skill.package.extra_rules || [];
  out.rules = (skill.rules || []).filter((r) => extra.includes(r)); // the package's own rules stay out
  out.package = { key: skill.package.key, version: skill.package.version, params: skill.package.params || {},
    extra_rules: extra };
  out.note = `ready-made skill: ${skill.package.key} v${skill.package.version}`;
  return out;
}

/** A tool as the file shows it: an app action only by its name in the catalog (schema and call stay
 *  Ultimo's), the customer's own tools (code, guard, http, e-mail …) in full; never the last test run. */
export function exportTool(tool) {
  if (tool.kind === 'app_action') {
    return { id: tool.id, name: tool.name, kind: tool.kind,
      config: { provider: tool.config?.provider ?? null, action: tool.config?.action ?? null },
      requires_confirmation: !!tool.requires_confirmation };
  }
  return withoutKeys(tool, TOOL_INTERNAL_FIELDS);
}

// keys whose value would be a credential if someone typed it in literally; placeholders ({{secrets.x}}) stay
const SECRET_KEY = /(authorization|api[-_]?key|apikey|token|secret|password|passwd|client[-_]?secret|bearer)/i;
// keys that name knowledge sources in a skill or a tool (old carried-over steps: config.action.knowledge.source_ids)
const SOURCE_LIST_KEYS = new Set(['source_ids', 'knowledge_ids', 'knowledge_source_ids']);
const SOURCE_ONE_KEYS = new Set(['source_id', 'knowledge_id', 'knowledge_source_id']);

/** A copy of `value` with literal credential values replaced; template placeholders are kept. */
export function redactSecrets(value) {
  if (Array.isArray(value)) return value.map(redactSecrets);
  if (!value || typeof value !== 'object') return value;
  const out = {};
  for (const [k, v] of Object.entries(value)) {
    if (SECRET_KEY.test(k) && typeof v === 'string' && v && !v.includes('{{')) out[k] = '[redacted]';
    else out[k] = redactSecrets(v);
  }
  return out;
}

/** Every knowledge source id named anywhere in `values`, in order of first appearance, without duplicates. */
export function referencedSourceIds(values) {
  const seen = new Set();
  const ids = [];
  const add = (id) => {
    if (id === null || id === undefined || id === '') return;
    const key = String(id);
    if (seen.has(key)) return;
    seen.add(key);
    ids.push(id);
  };
  const walk = (v) => {
    if (Array.isArray(v)) { v.forEach(walk); return; }
    if (!v || typeof v !== 'object') return;
    for (const [k, x] of Object.entries(v)) {
      if (SOURCE_LIST_KEYS.has(k) && Array.isArray(x)) x.forEach((id) => (typeof id === 'object' ? walk(id) : add(id)));
      else if (SOURCE_ONE_KEYS.has(k) && (typeof x === 'string' || typeof x === 'number')) add(x);
      else walk(x);
    }
  };
  walk(values);
  return ids;
}

/** The file name as stored: the last part of an uploaded file's address (uploads get a short suffix,
 *  e.g. datasheet_44f16b40.pdf), or null for websites (type url_link), FAQs and texts. */
export function storedFileName(doc) {
  if (!doc?.url || doc.type !== 'document') return null;
  try {
    const path = new URL(doc.url).pathname;
    const last = decodeURIComponent(path.split('/').filter(Boolean).pop() || '');
    return last || null;
  } catch (_) {
    return null;
  }
}

function sourceMapping(id, doc) {
  return {
    source_id: id,
    filename: doc ? doc.title : null,
    stored_filename: doc ? storedFileName(doc) : null,
    type: doc ? doc.type : null,
    found: !!doc,
  };
}

const withoutKeys = (obj, keys) => {
  const out = { ...obj };
  keys.forEach((k) => delete out[k]);
  return out;
};

/** { exported_at, bot_id, skill, tools, source_file_mappings } for one skill of `spec`. */
export function buildSkillExport(spec, docs, skillId, now = new Date()) {
  const skill = (spec.skills || []).find((s) => s.id === skillId);
  if (!skill) throw new Error(`skill ${skillId} not found`);
  const tools = (skill.tool_ids || []).map((id) => (spec.tools || []).find((x) => x.id === id)).filter(Boolean);
  const cleanSkill = redactSecrets(exportSkill(skill));
  const cleanTools = tools.map((x) => redactSecrets(exportTool(x)));
  const byId = new Map((docs || []).map((d) => [String(d.id), d]));
  let ids = referencedSourceIds([cleanSkill, cleanTools]);
  // no explicit sources: a skill that uses the knowledge searches all of it
  if (!ids.length && skill.uses_knowledge) ids = (docs || []).map((d) => d.id);
  return {
    format: EXPORT_FORMAT,
    exported_at: now.toISOString(),
    bot_id: spec.id,
    skill: cleanSkill,
    tools: cleanTools,
    source_file_mappings: ids.map((id) => sourceMapping(id, byId.get(String(id)))),
  };
}

/** The whole agent: { exported_at, bot_id, agent, source_file_mappings } with every knowledge source. */
export function buildAgentExport(spec, docs, now = new Date()) {
  const agent = withoutKeys(spec, AGENT_VIEW_FIELDS);
  agent.skills = (spec.skills || []).map(exportSkill);
  agent.tools = (spec.tools || []).map(exportTool);
  agent.integrations = (spec.integrations || []).map((i) => withoutKeys(i, INTEGRATION_INTERNAL_FIELDS));
  return {
    format: EXPORT_FORMAT,
    exported_at: now.toISOString(),
    bot_id: spec.id,
    agent: redactSecrets(agent),
    source_file_mappings: (docs || []).map((d) => sourceMapping(d.id, d)),
  };
}

/** A safe file name part: letters, digits, dashes. */
export function fileSlug(text) {
  const slug = String(text || '').normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase().slice(0, 60);
  return slug || 'export';
}

export const toJson = (data) => `${JSON.stringify(data, null, 2)}\n`;

/** Hands the browser a .json file to save. */
export function downloadJson(filename, data) {
  const blob = new Blob([toJson(data)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Fresh spec and knowledge list from the server (not the panel's cached copy), then the download.
 *  skillId null = the whole agent. */
export async function exportFromServer(botId, skillId = null) {
  const [spec, docs] = await Promise.all([studioApi.get(botId, ''), fetchDocs(botId)]);
  const day = new Date().toISOString().slice(0, 10);
  if (skillId) {
    const data = buildSkillExport(spec, docs.items, skillId);
    downloadJson(`skill-${fileSlug(data.skill.name)}-${day}.json`, data);
    return data;
  }
  const data = buildAgentExport(spec, docs.items);
  downloadJson(`agent-${fileSlug(spec.name)}-${day}.json`, data);
  return data;
}
